import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { FormField } from '@app/components/form-fields/FormField';
import useAgent from '@app/hooks/agents/useAgent';
import useAgentDocumentActions from '@app/hooks/agents/useAgentDocumentActions';
import { Button, List, Upload, message as antdMessage } from 'antd';
import type { UploadProps } from 'antd';

export type AssociatedDocumentsFieldProps = {
    agentId?: string;
};

const maxDocuments = 5;
const maxSizeMB = 20;
const acceptedDocumentTypes = ['application/pdf'];

export const AssociatedDocumentsField = ({ agentId }: AssociatedDocumentsFieldProps) => {
    const { data } = useAgent({ id: agentId ?? '' }, { enabled: Boolean(agentId) });
    const { uploadDocument, deleteDocument } = useAgentDocumentActions();

    const documents = data?.documents ?? [];
    const atLimit = documents.length >= maxDocuments;

    if (!agentId) {
        return (
            <FormField label="Associated Documents">
                <p className="form-hint">Save the agent first to upload associated documents.</p>
            </FormField>
        );
    }

    const handleBeforeUpload = (file: File) => {
        if (!acceptedDocumentTypes.includes(file.type)) {
            antdMessage.error('Only PDF files are supported');
            return Upload.LIST_IGNORE;
        }

        if (file.size / 1024 / 1024 > maxSizeMB) {
            antdMessage.error(`Document must be smaller than ${maxSizeMB}MB`);
            return Upload.LIST_IGNORE;
        }

        if (atLimit) {
            antdMessage.error(
                `A maximum of ${maxDocuments} documents can be associated with an agent`
            );
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const handleCustomRequest: UploadProps['customRequest'] = (options) => {
        // rc-upload's RcFile extends File; options.file is a File/Blob here since
        // beforeUpload never returns a Promise<File> that would swap it out.
        const file = options.file as File;
        uploadDocument.mutate(
            { agentId, file },
            {
                onSuccess: (response) => options.onSuccess?.(response),
                onError: (error) => options.onError?.(error as Error),
            }
        );
    };

    return (
        <FormField label="Associated Documents">
            <List
                size="small"
                bordered
                dataSource={documents}
                locale={{ emptyText: 'No documents uploaded yet' }}
                renderItem={(doc) => (
                    <List.Item
                        actions={[
                            <Button
                                key="delete"
                                type="text"
                                danger
                                aria-label={`Delete ${doc.filename}`}
                                icon={<DeleteOutlined />}
                                loading={
                                    deleteDocument.isPending &&
                                    deleteDocument.variables?.documentId === doc.id
                                }
                                onClick={() =>
                                    deleteDocument.mutate({ agentId, documentId: doc.id })
                                }
                            />,
                        ]}>
                        {doc.filename}
                    </List.Item>
                )}
            />
            <Upload
                accept="application/pdf"
                showUploadList={false}
                beforeUpload={handleBeforeUpload}
                customRequest={handleCustomRequest}
                disabled={atLimit || uploadDocument.isPending}>
                <Button
                    className="margin-top-10"
                    icon={<UploadOutlined />}
                    loading={uploadDocument.isPending}
                    disabled={atLimit}>
                    {atLimit ? `Maximum ${maxDocuments} documents reached` : 'Upload PDF'}
                </Button>
            </Upload>
        </FormField>
    );
};
